/**
 * Eros PLP filter tray (ADR-205) - PURE LOGIC. No DOM, no network, no globals read.
 * Everything here is a function of its arguments so it can be unit-tested in node
 * (see "node --check" + the harness notes in the ADR build record).
 *
 * Layers of the web feature (flat file names because Shopify assets have no folders):
 *   eros-plp-tray-icons.js  wire value -> icon          (data)
 *   eros-plp-tray-actives.js ingredient symbol + category tint  (data + pure)
 *   eros-plp-tray-price.js  price terciles / bucket labels                (pure, loaded first)
 *   eros-plp-tray-data.js   lazy fetches: collection prices, ingredient index (network + cache)
 *   eros-plp-tray-logic.js  THIS FILE: row map, dimming, labels, chips      (pure)
 *   eros-plp-tray-form.js   reads/writes the Prestige facet form        (DOM <-> S&D form)
 *   eros-plp-tray-view.js   builds the tray DOM from a view-model        (DOM rendering)
 *   eros-plp-filter-tray.js entry: state, events, re-render after facet:update
 *
 * Data comes from snippets/eros-plp-filter-tray.liquid (mirror of collection.filters).
 */
(function (root) {
  'use strict';
  var ns = (root.ErosPlpTray = root.ErosPlpTray || {});

  /* ---- ADR-205 2.2: rows per section family ------------------------------------------ */
  var ROW_MAP = {
    skin: {
      open: ['skin_types', 'brand', 'price'],
      folded: ['concerns', 'actives', 'free_from', 'safe_for_pregnancy', 'safe_for_sensitive', 'type', 'form']
    },
    hair: {
      open: ['hair_types', 'brand', 'price'],
      folded: ['concerns', 'free_from', 'safe_for_pregnancy', 'type', 'form']
    },
    makeup: {
      open: ['type', 'brand', 'price'],
      folded: ['cosmetic_finish', 'form']
    },
    other: {
      open: ['brand', 'price'],
      folded: ['type']
    }
  };

  var SECTION_FAMILY = {
    face_care: 'skin',
    body_care: 'skin',
    eye_care: 'skin',
    hair_care: 'hair',
    makeup: 'makeup'
  };

  /* row id -> Search & Discovery param. Metafield filters are filter.p.m.eros.<key>. */
  var ROW_PARAM = {
    brand: 'filter.p.vendor',
    price: 'filter.v.price',
    skin_types: 'filter.p.m.eros.skin_types',
    hair_types: 'filter.p.m.eros.hair_types',
    concerns: 'filter.p.m.eros.concerns',
    actives: 'filter.p.m.eros.actives',
    free_from: 'filter.p.m.eros.free_from',
    safe_for_pregnancy: 'filter.p.m.eros.safe_for_pregnancy',
    safe_for_sensitive: 'filter.p.m.eros.safe_for_sensitive',
    type: 'filter.p.m.eros.type',
    form: 'filter.p.m.eros.form',
    cosmetic_finish: 'filter.p.m.eros.cosmetic_finish'
  };
  var AVAILABILITY_PARAM = 'filter.v.availability';
  /* Optional boolean S&D filter backing the "عروض" bubble. Absent until the owner adds it. */
  var SALE_PARAM = 'filter.p.m.eros.on_sale';

  /* row id -> dictionary/icon group (strings.opt[group], icons.svg(group, ...)). */
  var ROW_GROUP = {
    skin_types: 'skin',
    hair_types: 'hair',
    concerns: 'concerns',
    free_from: 'free_from',
    safe_for_pregnancy: 'safety',
    safe_for_sensitive: 'safety',
    cosmetic_finish: 'finish',
    form: 'form',
    type: 'type',
    actives: 'actives',
    brand: 'brand'
  };
  /* Rows whose label is the S&D label itself (ingredient names, brand names). */
  var LABEL_FROM_SD = { actives: true, brand: true };
  /* "all" means "no restriction" - it is not a selectable target. */
  var HIDDEN_VALUES = { skin_types: { all: true }, hair_types: { all: true } };
  /* Sort: canonical order = dictionary order; others by count desc. */
  var COUNT_SORTED = { brand: true, concerns: true, actives: true, type: true, form: true };

  function familyOf(section) {
    return SECTION_FAMILY[section] || 'other';
  }

  /** Majority eros.section among a comma separated list ("face_care,face_care,makeup,"). */
  function majoritySection(csv) {
    var counts = {};
    var best = '';
    var parts = String(csv || '').split(',');
    for (var i = 0; i < parts.length; i++) {
      var s = parts[i].replace(/^\s+|\s+$/g, '');
      if (!s) continue;
      counts[s] = (counts[s] || 0) + 1;
      if (!best || counts[s] > counts[best]) best = s;
    }
    return best;
  }

  /** Every S&D param the family may show (legacy facet allowlist uses this). */
  function allowedParams(family) {
    var map = ROW_MAP[family] || ROW_MAP.other;
    var rows = map.open.concat(map.folded);
    var out = [AVAILABILITY_PARAM, ROW_PARAM.price, SALE_PARAM];
    for (var i = 0; i < rows.length; i++) out.push(ROW_PARAM[rows[i]]);
    return out;
  }

  function isAllowedParam(family, name) {
    if (!name) return false;
    var list = allowedParams(family);
    for (var i = 0; i < list.length; i++) {
      if (name === list[i] || name.indexOf(list[i] + '.') === 0) return true;
    }
    return false;
  }

  function findFilter(filters, param) {
    for (var i = 0; i < filters.length; i++) if (filters[i].p === param) return filters[i];
    return null;
  }

  /* ---- labels ------------------------------------------------------------------------ */
  /** Arabic/en/ku label for a wire value, or '' when the dictionary does not know it. */
  function labelFor(rowId, value, sdLabel, strings) {
    if (LABEL_FROM_SD[rowId]) return sdLabel || '';
    var group = ROW_GROUP[rowId];
    var dict = strings && strings.opt && strings.opt[group];
    return (dict && dict[value]) || '';
  }

  /* ---- options of one dictionary/brand row ------------------------------------------- */
  function buildOptions(rowId, filter, strings) {
    var out = [];
    var vals = (filter && filter.v) || [];
    var hidden = HIDDEN_VALUES[rowId] || {};
    var missing = [];
    for (var i = 0; i < vals.length; i++) {
      var v = vals[i];
      if (hidden[v.v]) continue;
      var active = v.a === 1;
      /* ADR 2.2: options with 0 products in the collection are hidden; an option that is
         only 0 because of the OTHER selections is dimmed (S&D keeps it with count 0). The
         collection-wide zero is not knowable from S&D, so: count 0 + not active = dimmed. */
      if (rowId === 'brand' && dimOption(v.c, active)) continue; /* brand row: only brands present */
      var label = labelFor(rowId, v.v, v.l, strings);
      if (!label) {
        missing.push(v.v);
        continue;
      }
      out.push({
        value: v.v,
        label: label,
        count: v.c,
        active: active,
        dim: dimOption(v.c, active),
        image: v.i || '',
        gid: v.v,
        order: i
      });
    }
    if (COUNT_SORTED[rowId]) {
      out.sort(function (a, b) {
        return b.count - a.count || a.order - b.order;
      });
    } else {
      var canon = canonicalOrder(rowId, strings);
      out.sort(function (a, b) {
        var ia = canon[a.value] === undefined ? 999 : canon[a.value];
        var ib = canon[b.value] === undefined ? 999 : canon[b.value];
        return ia - ib || a.order - b.order;
      });
    }
    out.missing = missing;
    return out;
  }

  function canonicalOrder(rowId, strings) {
    var dict = (strings && strings.opt && strings.opt[ROW_GROUP[rowId]]) || {};
    var idx = {};
    var n = 0;
    for (var k in dict) if (Object.prototype.hasOwnProperty.call(dict, k)) idx[k] = n++;
    return idx;
  }

  /** ADR 2.1: dimmed (not removed) when the option would give 0 products. */
  function dimOption(count, active) {
    return !active && !(count > 0);
  }

  /* Rows that get a search circle first: row id -> the ui string keys of its label/placeholder. */
  var SEARCH_ROWS = {
    brand: { label: 'brand_search', placeholder: 'brand_search_placeholder' },
    actives: { label: 'actives_search', placeholder: 'actives_search_placeholder' }
  };

  /**
   * Text filter of a searchable row (brand / actives). Matches the shown (Arabic) label and
   * `opt.alt` (extra searchable text, e.g. the English ingredient name). Empty query = all.
   * The brand row additionally keeps only brands with products and sorts by count (buildOptions).
   */
  function filterOptions(options, query) {
    var q = normalizeQuery(query);
    if (!q) return options;
    var out = [];
    for (var i = 0; i < options.length; i++) {
      var o = options[i];
      if (normalizeQuery(o.label).indexOf(q) !== -1 || normalizeQuery(o.alt).indexOf(q) !== -1) out.push(o);
    }
    return out;
  }
  var filterBrands = filterOptions;

  /* Search folding: lowercase, drop Arabic diacritics, unify alef forms (escapes keep the
     hardcoded-Arabic lint gate quiet - these are matching rules, not UI copy). */
  var RE_DIACRITICS = new RegExp('[\\u064B-\\u065F\\u0670]', 'g');
  var RE_ALEF_FORMS = new RegExp('[\\u0623\\u0625\\u0622]', 'g');
  function normalizeQuery(s) {
    return String(s || '')
      .toLowerCase()
      .replace(RE_DIACRITICS, '')
      .replace(RE_ALEF_FORMS, String.fromCharCode(0x0627))
      .replace(/^\s+|\s+$/g, '');
  }

  /* ---- rows to show ------------------------------------------------------------------ */
  /**
   * Which rows exist for this page and which of them start open.
   * available(rowId) is decided by the caller from the data (filter present with options).
   * Open = the first N available rows, N = ADR open count of the family (3, or 2 for "other").
   * Returns [{ id, open }] in display order.
   */
  function layoutRows(family, availableFn) {
    var map = ROW_MAP[family] || ROW_MAP.other;
    var order = map.open.concat(map.folded);
    var rows = [];
    for (var i = 0; i < order.length; i++) if (availableFn(order[i])) rows.push(order[i]);
    var openCount = Math.min(map.open.length, rows.length);
    var out = [];
    for (var j = 0; j < rows.length; j++) out.push({ id: rows[j], open: j < openCount });
    return out;
  }

  /* ---- selection summary + chips ----------------------------------------------------- */
  function fillTpl(s, map) {
    return String(s || '').replace(/\{(\w+)\}/g, function (m, k) {
      return map[k] === undefined ? m : map[k];
    });
  }

  /** Folded layer summary: "المكوّنات: نياسيناميد" (max 2 names then "و3 غيرها"). */
  function summaryText(title, labels, ui) {
    if (!labels.length) return '';
    var shown = labels.slice(0, 2).join('، ');
    if (labels.length > 2) shown += ' ' + fillTpl(ui.more, { n: labels.length - 2 });
    return title + ': ' + shown;
  }

  /**
   * Removable chips for every active selection.
   * chip = { kind:'value'|'price'|'stock'|'sale', param, value, label }
   */
  function chipsFor(data, strings) {
    var out = [];
    var ui = strings.ui || {};
    var filters = data.filters || [];
    var rowByParam = {};
    for (var r in ROW_PARAM) if (Object.prototype.hasOwnProperty.call(ROW_PARAM, r)) rowByParam[ROW_PARAM[r]] = r;
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      if (f.t === 'price') {
        if (f.sg !== '' || f.sl !== '') out.push({ kind: 'price', param: f.p, value: '', label: ns.price.chipLabel(f, ui) });
        continue;
      }
      if (f.p === AVAILABILITY_PARAM) {
        if (f.a) out.push({ kind: 'stock', param: f.p, value: '1', label: ui.in_stock });
        continue;
      }
      if (f.p === SALE_PARAM) {
        if (f.a) out.push({ kind: 'sale', param: f.p, value: firstActiveValue(f), label: ui.on_sale });
        continue;
      }
      var row = rowByParam[f.p];
      if (!row) continue;
      for (var j = 0; j < f.v.length; j++) {
        if (f.v[j].a !== 1) continue;
        var label = labelFor(row, f.v[j].v, f.v[j].l, strings) || f.v[j].l;
        out.push({ kind: 'value', param: f.p, value: f.v[j].v, label: label });
      }
    }
    return out;
  }

  function firstActiveValue(f) {
    for (var i = 0; i < f.v.length; i++) if (f.v[i].a === 1) return f.v[i].v;
    return '1';
  }

  /** Count shown on the «فلاتر (n)» button = number of removable chips. */
  function activeCount(data, strings) {
    return chipsFor(data, strings).length;
  }

  var api = {
    ROW_MAP: ROW_MAP,
    ROW_PARAM: ROW_PARAM,
    ROW_GROUP: ROW_GROUP,
    AVAILABILITY_PARAM: AVAILABILITY_PARAM,
    SALE_PARAM: SALE_PARAM,
    familyOf: familyOf,
    majoritySection: majoritySection,
    allowedParams: allowedParams,
    isAllowedParam: isAllowedParam,
    findFilter: findFilter,
    labelFor: labelFor,
    buildOptions: buildOptions,
    dimOption: dimOption,
    SEARCH_ROWS: SEARCH_ROWS,
    filterOptions: filterOptions,
    filterBrands: filterBrands,
    layoutRows: layoutRows,
    summaryText: summaryText,
    chipsFor: chipsFor,
    activeCount: activeCount
  };
  ns.logic = api;
  /* the legacy facet allowlist (eros-plp-facet-allowlist.js) reads this global */
  root.ErosPlpTrayLogic = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
